import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Form,
  InputGroup,
  Spinner,
  Alert,
} from "react-bootstrap";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function UsersClockEntriesPage() {
  const [users, setUsers] = useState([]);
  const [clockEntries, setClockEntries] = useState([]);
  const [weeklyHours, setWeeklyHours] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [rolePermissions, setRolePermissions] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState(null);

  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const userDocRef = doc(db, "Users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userRole = userDoc.data().role || "";
            setCurrentUserRole(userRole);

            if (userRole === "admin") {
              setRolePermissions({ canViewClockInOut: true });
            } else {
              const roleDocRef = doc(db, "Roles", userRole);
              const roleDoc = await getDoc(roleDocRef);

              if (roleDoc.exists()) {
                setRolePermissions(roleDoc.data().permissions);
              } else {
                setRolePermissions(null);
              }
            }
          }
        }
      });
    };

    fetchUserRole();
  }, []);

  useEffect(() => {
    if (
      rolePermissions &&
      !rolePermissions.canViewClockInOut &&
      currentUserRole !== "admin"
    ) {
      setAccessDenied(true);
    }
  }, [rolePermissions, currentUserRole]);

  useEffect(() => {
    const fetchUsersAndEntries = async () => {
      setLoading(true);
      setError(null);
      try {
        const usersSnapshot = await getDocs(collection(db, "Users"));
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersData);

        const clockEntriesSnapshot = await getDocs(collection(db, "clock_entries"));
        const entriesData = clockEntriesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClockEntries(entriesData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUsersAndEntries();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (text) => {
    setSearchText(text);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDropdownVisible(true);
    }, 300); // debounce 300ms
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchText(`${user.fullName} (${user.email})`);
    setDropdownVisible(false);
    if (selectedMonth) {
      calculateWeeklyHours(user, selectedMonth);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    if (selectedUser) {
      calculateWeeklyHours(selectedUser, month);
    }
  };

  const calculateWeeklyHours = useCallback((user, month) => {
    if (!user) {
      setWeeklyHours([]);
      setFilteredEntries([]);
      return;
    }

    const userEntries = clockEntries.filter(
      (entry) => entry.email === user.email
    );

    let filteredEntries = [];
    const weeks = Array(5).fill(0); // For maximum 5 weeks in a month

    if (month) {
      const startOfMonth = new Date(`${month}-01T00:00:00`);
      const endOfMonth = new Date(startOfMonth.getFullYear(), startOfMonth.getMonth() + 1, 0, 23, 59, 59);

      userEntries.forEach((entry) => {
        const clockIn = new Date(entry.clockIn);
        const clockOut = entry.clockOut ? new Date(entry.clockOut) : null;

        if (
          clockIn >= startOfMonth &&
          clockIn <= endOfMonth &&
          clockOut &&
          clockOut >= clockIn
        ) {
          const weekIndex = Math.floor((clockIn.getDate() - 1) / 7); // Determine the week number
          const duration = (clockOut - clockIn) / 1000 / 60 / 60; // Duration in hours

          // Calculate total break duration in hours
          const totalBreaks = (entry.breaks || []).reduce(
            (sum, breakItem) => sum + ((breakItem.duration || 0) / 60),
            0
          );

          weeks[weekIndex] += duration - totalBreaks;

          // Add entry to filtered entries
          filteredEntries.push({
            clockIn: clockIn.toISOString(),
            clockOut: clockOut.toISOString(),
            duration: (duration - totalBreaks).toFixed(2),
            breaks: (totalBreaks * 60).toFixed(0), // Total break duration in minutes
          });
        }
      });
    } else {
      // No month selected, show all entries for user
      filteredEntries = userEntries.map((entry) => {
        const clockIn = new Date(entry.clockIn);
        const clockOut = entry.clockOut ? new Date(entry.clockOut) : null;

        const duration = clockOut && clockOut >= clockIn ? (clockOut - clockIn) / 1000 / 60 / 60 : 0;

        const totalBreaks = (entry.breaks || []).reduce(
          (sum, breakItem) => sum + ((breakItem.duration || 0) / 60),
          0
        );

        return {
          clockIn: clockIn.toISOString(),
          clockOut: clockOut ? clockOut.toISOString() : "",
          duration: (duration - totalBreaks).toFixed(2),
          breaks: (totalBreaks * 60).toFixed(0),
        };
      });
    }

    setWeeklyHours(weeks.map((hours) => hours.toFixed(2))); // Round to 2 decimals
    // Sort filteredEntries by clockIn descending before setting filteredEntries state
    filteredEntries.sort((a, b) => new Date(b.clockIn) - new Date(a.clockIn));
    setFilteredEntries(filteredEntries);
  }, [clockEntries]);

  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <h1 className="text-center text-danger">403 Forbidden</h1>
          <p className="text-center">You do not have permission to access this page.</p>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-time-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">User Clock Entries</h1>
          </div>
          <p className="lead text-muted">View and manage employee clock entries and weekly hours</p>
        </div>

        {loading ? (
          <div className="text-center my-5">
            <div className="mb-4">
              <Spinner animation="border" variant="primary" size="lg" />
            </div>
            <h5 className="text-muted mb-3">Loading Clock Data</h5>
            <p className="text-muted">Please wait while we fetch user clock entries...</p>
          </div>
        ) : error ? (
          <div className="bg-gradient-danger text-white rounded-4 p-4 shadow-lg mb-5"
               style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)' }}>
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-4 me-3"></i>
              <div>
                <h6 className="mb-1 fw-bold">Error Loading Data</h6>
                <p className="mb-0 small">{error}</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Enhanced Search and Month Selection */}
            <Card className="mb-5 border-0 shadow-lg rounded-4">
              <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-search-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Search & Filter</h5>
                </div>
              </div>
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col lg={6} ref={dropdownRef} className="position-relative">
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <i className="ri-user-search-line me-2"></i>Search by User
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Type to search users..."
                        value={searchText}
                        onChange={(e) => handleSearch(e.target.value)}
                        onFocus={() => setDropdownVisible(true)}
                        className="form-control-lg shadow-sm border-2"
                        style={{ borderRadius: '12px' }}
                      />
                      {dropdownVisible && searchText.trim() !== "" && (
                        <div className="dropdown-menu w-100 show rounded-3 shadow-lg border-0">
                          {users
                          .filter((user) =>
                            user.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
                            user.email.toLowerCase().includes(searchText.toLowerCase())
                          )
                          .map((user) => (
                            <div
                              key={user.id}
                              className="dropdown-item py-3 rounded-2 mx-2"
                              onClick={() => handleUserSelect(user)}
                              style={{ cursor: "pointer", fontWeight: "500" }}
                            >
                              <i className="ri-user-line me-2"></i>
                              {user.fullName} ({user.email})
                            </div>
                          ))}
                          {users.filter((user) =>
                            user.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
                            user.email.toLowerCase().includes(searchText.toLowerCase())
                          ).length === 0 && (
                            <div className="dropdown-item py-3 text-muted">
                              <i className="ri-search-line me-2"></i>No users found
                            </div>
                          )}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group>
                      <Form.Label className="fw-semibold">
                        <i className="ri-calendar-line me-2"></i>Select Month
                      </Form.Label>
                      <Form.Control
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => handleMonthChange(e.target.value)}
                        className="form-control-lg shadow-sm border-2"
                        style={{ borderRadius: '12px' }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Enhanced Weekly Hours Section */}
            {selectedUser && selectedMonth && (
              <Card className="border-0 shadow-lg rounded-4 mb-5">
                <div className="bg-gradient-success text-white p-4 rounded-top-4" 
                     style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <i className="ri-calendar-week-line fs-4 me-3"></i>
                      <h5 className="mb-0 fw-bold">Weekly Hours for {selectedUser.fullName}</h5>
                    </div>
                    <span className="badge bg-white text-success px-3 py-2 rounded-pill">
                      {selectedMonth}
                    </span>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="table-responsive">
                    <Table className="table-hover table-borderless">
                      <thead className="bg-primary text-white">
                        <tr>
                          <th className="border-0">
                            <i className="ri-calendar-line me-2"></i>Week
                          </th>
                          <th className="border-0">
                            <i className="ri-time-line me-2"></i>Hours Worked
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklyHours.map((hours, index) => (
                          <tr key={index} className="border-bottom">
                            <td className="fw-semibold">Week {index + 1}</td>
                            <td>
                              <span className="badge bg-success rounded-pill px-3 py-2">
                                {hours} hours
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Enhanced Clock Entries Section */}
            {filteredEntries.length > 0 && (
              <Card className="border-0 shadow-lg rounded-4">
                <div className="bg-gradient-info text-white p-4 rounded-top-4" 
                     style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <i className="ri-history-line fs-4 me-3"></i>
                      <h5 className="mb-0 fw-bold">Clock Entries for {selectedUser.fullName}</h5>
                    </div>
                    <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                      {filteredEntries.length} entries
                    </span>
                  </div>
                </div>
                <Card.Body className="p-4">
                  <div className="table-responsive">
                    <Table className="table-hover table-borderless">
                      <thead className="bg-primary text-white">
                        <tr>
                          <th className="border-0">
                            <i className="ri-login-circle-line me-2"></i>Clock In
                          </th>
                          <th className="border-0">
                            <i className="ri-logout-circle-line me-2"></i>Clock Out
                          </th>
                          <th className="border-0">
                            <i className="ri-time-line me-2"></i>Duration
                          </th>
                          <th className="border-0">
                            <i className="ri-pause-circle-line me-2"></i>Breaks
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEntries.map((entry, index) => (
                          <tr key={index} className="border-bottom">
                            <td className="fw-semibold">
                              {entry.clockIn && !isNaN(new Date(entry.clockIn)) 
                                ? new Date(entry.clockIn).toLocaleString() 
                                : "N/A"}
                            </td>
                            <td className="fw-semibold">
                              {entry.clockOut && !isNaN(new Date(entry.clockOut)) 
                                ? new Date(entry.clockOut).toLocaleString() 
                                : "N/A"}
                            </td>
                            <td>
                              <span className="badge bg-success rounded-pill px-3 py-2">
                                {entry.duration} hrs
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-warning text-dark rounded-pill px-3 py-2">
                                {entry.breaks} min
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Empty State */}
            {selectedUser && selectedMonth && filteredEntries.length === 0 && (
              <Card className="border-0 shadow-lg rounded-4">
                <Card.Body className="p-5 text-center">
                  <div className="mb-4">
                    <i className="ri-time-line text-muted" style={{ fontSize: '4rem' }}></i>
                  </div>
                  <h5 className="text-muted mb-3">No Clock Entries Found</h5>
                  <p className="text-muted">No clock entries found for {selectedUser.fullName} in {selectedMonth}.</p>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
      <Footer />
    </React.Fragment>
  );
}
